import { Component, OnDestroy, OnInit, Input } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { EntityListComponent } from 'app/components/entity-list/entity-list.component';
import { UserTimePipe } from 'app/components/shared/UserTimePipe';
import { AuthenticationService } from 'app/core/services/authentication/authentication.service';
import { DriverService } from 'app/modules/vehicle-inventory/services/driver.service';
import { RestVehicleInventory } from 'app/modules/vehicle-inventory/services/rest_vehicle-inventory';
import { InvVehicleService } from 'app/modules/vehicle-inventory/services/invVehicle.service';
import { Rest } from 'app/rest/rest_client';
import { I18nService } from 'app/services/i18n/i18n.service';
import { LoadingService } from 'app/services/loading-service/loading.service';
import { NotificationsService, Severity } from 'app/services/notifications-service/notifications.service';
import { ConfirmationService, SelectItem } from 'primeng/api';
import { VehicleService } from 'app/rest/vehicle.service';
import { VehiclesgroupService } from 'app/rest/vehiclesgroup.service';
import { VehicleUsageService } from 'app/rest/vehicleUsage.service';
import { FleetService } from 'app/rest/fleet.service';
import { ExcelService } from "app/services/excel-service/excel.service";
import { VehicleHtcService } from 'app/modules/vehicle-inventory/services/vehicle-htc.service';
import { FineService } from 'app/rest/fine.service';
import { DocumentationService } from 'app/modules/vehicle-inventory/services/documentation.service';
import { VehiclebrandService } from 'app/rest/vehiclebrand.service';
import { VehiclemodelService } from 'app/rest/vehiclemodel.service';
import { VehicleversionService } from 'app/rest/vehicleversion.service';
import { FinancingService } from 'app/modules/vehicle-inventory/services/table-management/financing.service';
import { Subscription } from 'rxjs';
import { VehicleAccessoryService } from 'app/modules/vehicle-inventory/services/vehicle-accessory.service';
import { AccessoryService } from 'app/rest/accessory.service';
import { CardService } from 'app/rest/card.service';
import { DelegationService } from 'app/modules/vehicle-inventory/services/table-management/delegation.service';
import { SubdelegationService } from 'app/rest/subdelegation.service';
import { DatePipe } from '@angular/common';

@Component({
    selector: 'app-vehicle-list',
    templateUrl: './vehicle-list.component.html',
    styleUrls: ['./vehicle-list.component.css'],
    providers: [
        InvVehicleService,
        DriverService,
        VehicleService,
        VehiclesgroupService,
        VehicleUsageService,
        FleetService,
        VehiclesgroupService,
        ExcelService,
        VehicleHtcService,
        FineService,
        FinancingService,
        DocumentationService,
        VehiclebrandService,
        VehiclemodelService,
        VehicleversionService,
        VehicleAccessoryService,
        AccessoryService,
        DelegationService,
        SubdelegationService
    ],
    standalone: false
})
export class VehicleListComponent extends EntityListComponent<Rest.Vehicle> implements OnInit, OnDestroy {
  @Input() driver?: Rest.InvDriver = null;

  user: Rest.User;
  EnabledFilter: SelectItem[];

  /** Params **/
  isNew: boolean = false;
  selectedVehicle: Rest.Vehicle;

  /** Vehicle Inventory GET_PAGE FILTERS **/
  selectedClient: number = 394971; //CIRSA

  brandList: Rest.VehicleBrand[] = [];
  modelList: Rest.VehicleModel[] = [];
  versionList: Rest.VehicleVersion[] = [];
  vehicleTypeList: Rest.VehicleType[] = [];
  vehicleStatusList: Rest.VehicleStatus[] = [];
  companiesList: Rest.BillingAccount[] = [];
  delegationList: Rest.Delegation[] = [];
  subdelegationList: Rest.Subdelegation[] = [];
  updateListSubscription: Subscription;

  vehicles: Rest.Vehicle[] = [];

  constructor(
    private objectService: InvVehicleService,
    protected confirmationService: ConfirmationService,
    protected notificationsService: NotificationsService,
    protected i18n: I18nService,
    protected authenticationService: AuthenticationService,
    protected loadingService: LoadingService,
    protected userTime: UserTimePipe,
    private translateService: TranslateService,
    protected excelService: ExcelService,
    protected vehiclebrandService: VehiclebrandService,
    protected vehiclemodelService: VehiclebrandService,
    protected vehicleversionService: VehiclebrandService,
    private delegationService: DelegationService,
    private subdelegationService: SubdelegationService,
    private datePipe: DatePipe,
  ) {
    super(
      objectService,
      confirmationService,
      notificationsService,
      i18n,
      authenticationService,
      loadingService,
      userTime,
      excelService
    );
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.updateListSubscription = this.objectService.vehicleSaved
      .subscribe(
        () => {
          this.refresh();
        }
      );
    this.getBrandsByClient(this.selectedClient);
    this.getModelsByClient(this.selectedClient);
    this.getVersionsByClient(this.selectedClient);
    this.getVehicleTypes();
    this.getVehicleStatus(this.selectedClient);
    this.getCompanies(this.selectedClient);
    this.getDelegationList(this.selectedClient);
    this.getSubdelegationList(this.selectedClient);
  }

  ngOnDestroy(): void {
    this.updateListSubscription.unsubscribe();
  }

  afterLoad() {
    this.paginationResult.entities.forEach((cl: any) => {
      /*Permissions */
      cl._$changeStatus = true; //This is for the validation of entity-list (selectAll function)
      cl._$update = true;
      /** */
    });
    /*Permissions */
    this.checkSelectAll();
  }

  protected beforeLoad() {
    if (this.driver !== null) {
      this.paginationRequest.filters["client"] = [this.driver.id.toString()];

      this.driver.driverAssignments.forEach((assignment) => {
        this.vehicles.push(assignment.vehicle);
      });
    }
  }

  excelColumns = {};

  exportExcel() {
    this.setExcelColumns();
    let fileNameTitle = this.translateService.instant('tableManagement.vehicle.vehiclesManagement');
    super.exportExcelWithColumns(fileNameTitle, this.excelColumns);
  }

  setExcelColumns() {
    const name = (entity: any) => entity.name ?? ''
    const brand = (entity: any) => entity.model?.brand?.name ?? ''
    const model = (entity: any) => entity.model?.name ?? ''
    const version = (entity: any) => entity.version?.name ?? ''
    const modelType = (entity: any) => entity.model?.type?.name ?? ''
    const frameNumber = (entity: any) => entity.frameNumber ?? ''
    const vehicleStatus = (entity: any) => entity.status?.code ?? ''
    const billingAccount = (entity: any) => entity.billingAccount?.name ?? ''

    this.excelColumns = {
      [this.translateService.instant('control-panel.vehicles.licensePlate')]: { bindField: name },
      [this.translateService.instant('tableManagement.brands.brand')]: { bindField: brand },
      [this.translateService.instant('tableManagement.models.model')]: { bindField: model },
      [this.translateService.instant('tableManagement.versions.version')]: { bindField: version },
      [this.translateService.instant('tableManagement.models.type')]: { bindField: modelType },
      [this.translateService.instant('control-panel.vehicles.frameNumber')]: { bindField: frameNumber },
      [this.translateService.instant('tableManagement.vehicleStatus.vehicleStatus')]: { bindField: vehicleStatus },
      [this.translateService.instant('tableManagement.companies.company')]: { bindField: billingAccount }
    }
  }

  public getBrandsByClient(clientId: number) {
    const promise = new Promise<void>((resolve, reject) => {
      const that = this;
      this.objectService
        .getBrands(clientId)
        .then(
          function (response) {
            that.brandList = response;
            that.brandList.sort((a, b) => a.name > b.name ? 1 : -1);
          },
          /* failure */
          function (error) {
            console.log('The request failed: ' + error);
            resolve();
          }
        )
        .catch(function (reason) {
          console.log('Catched: ' + reason);
          resolve();
        });
    });
  }

  public getModelsByClient(clientId: number) {
    const promise = new Promise<void>((resolve, reject) => {
      const that = this;
      this.objectService.getModelsByClient(clientId)
        .then(
          function (response) {
            that.modelList = response;
            that.modelList.sort((a, b) => a.name > b.name ? 1 : -1);
          },
          /* failure */
          function (error) {
            console.log('The request failed: ' + error);
            resolve();
          },
        )
        .catch(function (reason) {
          console.log('Catched: ' + reason);
          resolve();
        });
    });
  }

  public getVersionsByClient(clientId: number) {
    const promise = new Promise<void>((resolve, reject) => {
      const that = this;
      this.objectService.getVersionsByClient(clientId)
        .then(
          function (response) {
            that.versionList = response;
            that.versionList.sort((a, b) => a.name > b.name ? 1 : -1);
          },
          /* failure */
          function (error) {
            console.log('The request failed: ' + error);
            resolve();
          },
        )
        .catch(function (reason) {
          console.log('Catched: ' + reason);
          resolve();
        });
    });
  }

  public getVehicleTypes() {
    const promise = new Promise<void>((resolve, reject) => {
      const that = this;
      this.objectService.getVehicleTypes()
        .then(
          function (response) {
            that.vehicleTypeList = response;
            that.vehicleTypeList.sort((a, b) => a.name > b.name ? 1 : -1);
          },
          /* failure */
          function (error) {
            console.log('The request failed: ' + error);
            resolve();
          },
        )
        .catch(function (reason) {
          console.log('Catched: ' + reason);
          resolve();
        });
    });
  }

  public getVehicleStatus(clientId: number) {
    const promise = new Promise<void>((resolve, reject) => {
      const that = this;
      this.objectService.getVehicleStatus(clientId)
        .then(
          function (response) {
            that.vehicleStatusList = response;
            that.vehicleStatusList.sort((a, b) => a.name > b.name ? 1 : -1);
          },
          /* failure */
          function (error) {
            console.log('The request failed: ' + error);
            resolve();
          },
        )
        .catch(function (reason) {
          console.log('Catched: ' + reason);
          resolve();
        });
    });
  }

  public getCompanies(clientId: number) {
    const promise = new Promise<void>((resolve, reject) => {
      const that = this;
      this.objectService.getBillingAccounts(clientId)
        .then(
          function (response) {
            that.companiesList = response;
            that.companiesList.sort((a, b) => a.name > b.name ? 1 : -1);
          },
          /* failure */
          function (error) {
            console.log('The request failed: ' + error);
            resolve();
          },
        )
        .catch(function (reason) {
          console.log('Catched: ' + reason);
          resolve();
        });
    });
  }

  public getDelegationList(clientId: number) {
    const promise = new Promise<void>((resolve, reject) => {
      const that = this;
      this.delegationService
        .getList(clientId)
        .then(
          function (response) {
            that.delegationList = response;
          },
          /* failure */
          function (error) {
            console.log('The request failed: ' + error);
            resolve();
          },
        )
        .catch(function (reason) {
          console.log('Catched: ' + reason);
          resolve();
        });
    });
  }

  public getSubdelegationList(clientId: number) {
    const promise = new Promise<void>((resolve, reject) => {
      const that = this;
      this.subdelegationService
        .getListByClient(clientId)
        .then(
          function (response) {
            that.subdelegationList = response;
            console.log(that.subdelegationList);
          },
          /* failure */
          function (error) {
            console.log('The request failed: ' + error);
            resolve();
          },
        )
        .catch(function (reason) {
          console.log('Catched: ' + reason);
          resolve();
        });
    });
  }
}

